/**
 * Store Selector Hooks
 *
 * Custom hooks that select specific slices of Zustand store state
 * for optimized re-renders. Components using these hooks will only
 * re-render when the selected slice changes.
 *
 * @example
 * ```ts
 * // Instead of:
 * const { user } = useAuthStore(); // Re-renders on any auth state change
 *
 * // Use:
 * const user = useAuthUser(); // Only re-renders when user changes
 * ```
 */

import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { useWorkspaceStore } from '../stores/workspaceStore';
import { useNotificationStore } from '../stores/notificationStore';
import { useSidebarStore } from '../stores/sidebarStore';
import type { User, AuthTokens, ThemeMode, Workspace, WorkspaceMemberRole, Notification } from '@mellify/shared';

// ─── Auth Selectors ─────────────────────────────────────────────────────────

/** Select the authenticated user. Returns null if not authenticated. */
export const useAuthUser = (): User | null => {
  return useAuthStore((state) => state.user);
};

/** Select the auth tokens. Returns null if not authenticated. */
export const useAuthTokens = (): AuthTokens | null => {
  return useAuthStore((state) => state.tokens);
};

/** Select whether the user is authenticated. */
export const useIsAuthenticated = (): boolean => {
  return useAuthStore((state) => state.isAuthenticated);
};

/** Select whether auth state is loading. */
export const useAuthLoading = (): boolean => {
  return useAuthStore((state) => state.isLoading);
};

/** Select auth error message. */
export const useAuthError = (): string | null => {
  return useAuthStore((state) => state.error);
};

/** Select auth actions (login, logout, etc.). Stable reference - never causes re-render. */
export const useAuthActions = () => {
  return useAuthStore((state) => ({
    login: state.login,
    logout: state.logout,
    setUser: state.setUser,
    setTokens: state.setTokens,
    setLoading: state.setLoading,
    setError: state.setError,
    updateUser: state.updateUser,
  }));
};

// ─── UI Selectors ───────────────────────────────────────────────────────────

/** Select the current theme mode. */
export const useThemeMode = (): ThemeMode => {
  return useUIStore((state) => state.theme);
};

/** Select the resolved theme (light or dark). */
export const useResolvedTheme = (): 'light' | 'dark' => {
  return useUIStore((state) => state.resolvedTheme);
};

/** Select whether the current resolved theme is dark. */
export const useIsDarkMode = (): boolean => {
  return useUIStore((state) => state.resolvedTheme === 'dark');
};

/** Select whether a specific modal is open. */
export const useIsModalOpen = (modalId: string): boolean => {
  return useUIStore((state) => !!state.modals[modalId]);
};

/** Select whether the command palette is open. */
export const useIsCommandPaletteOpen = (): boolean => {
  return useUIStore((state) => state.commandPaletteOpen);
};

/** Select whether search is focused. */
export const useIsSearchFocused = (): boolean => {
  return useUIStore((state) => state.searchFocused);
};

/** Select UI actions. Stable reference - never causes re-render. */
export const useUIActions = () => {
  return useUIStore((state) => ({
    setTheme: state.setTheme,
    toggleTheme: state.toggleTheme,
    openModal: state.openModal,
    closeModal: state.closeModal,
    toggleModal: state.toggleModal,
    closeAllModals: state.closeAllModals,
    toggleCommandPalette: state.toggleCommandPalette,
    setCommandPaletteOpen: state.setCommandPaletteOpen,
    setSearchFocused: state.setSearchFocused,
  }));
};

// ─── Sidebar Selectors ──────────────────────────────────────────────────────

/** Select whether the sidebar is collapsed. */
export const useIsSidebarCollapsed = (): boolean => {
  return useSidebarStore((state) => state.isCollapsed);
};

/** Select whether the mobile sidebar is open. */
export const useIsMobileSidebarOpen = (): boolean => {
  return useSidebarStore((state) => state.isMobileOpen);
};

/** Select sidebar actions. Stable reference. */
export const useSidebarActions = () => {
  return useSidebarStore((state) => ({
    toggle: state.toggle,
    collapse: state.collapse,
    expand: state.expand,
    openMobile: state.openMobile,
    closeMobile: state.closeMobile,
    toggleMobile: state.toggleMobile,
  }));
};

// ─── Workspace Selectors ────────────────────────────────────────────────────

/** Select the current workspace. Returns null if none selected. */
export const useCurrentWorkspace = (): Workspace | null => {
  return useWorkspaceStore((state) => state.currentWorkspace);
};

/** Select the current workspace ID. Returns undefined if none selected. */
export const useCurrentWorkspaceId = (): string | undefined => {
  return useWorkspaceStore((state) => state.currentWorkspace?.id);
};

/** Select the current user's role in the active workspace. */
export const useCurrentMemberRole = (): WorkspaceMemberRole | null => {
  return useWorkspaceStore((state) => state.currentMemberRole);
};

/** Select the list of workspaces. */
export const useWorkspaces = (): Workspace[] => {
  return useWorkspaceStore((state) => state.workspaces);
};

/** Select workspace loading state. */
export const useWorkspaceLoading = (): boolean => {
  return useWorkspaceStore((state) => state.isLoading);
};

/** Select workspace error. */
export const useWorkspaceError = (): string | null => {
  return useWorkspaceStore((state) => state.error);
};

/** Select workspace actions. Stable reference. */
export const useWorkspaceActions = () => {
  return useWorkspaceStore((state) => ({
    setCurrentWorkspace: state.setCurrentWorkspace,
    setCurrentMemberRole: state.setCurrentMemberRole,
    setWorkspaces: state.setWorkspaces,
    addWorkspace: state.addWorkspace,
    removeWorkspace: state.removeWorkspace,
    updateWorkspace: state.updateWorkspace,
    setLoading: state.setLoading,
    setError: state.setError,
  }));
};

// ─── Notification Selectors ─────────────────────────────────────────────────

/** Select the notification list. */
export const useNotifications = (): Notification[] => {
  return useNotificationStore((state) => state.notifications);
};

/** Select the unread notification count. */
export const useUnreadNotificationCount = (): number => {
  return useNotificationStore((state) => state.unreadCount);
};

/** Select whether there are unread notifications. */
export const useHasUnreadNotifications = (): boolean => {
  return useNotificationStore((state) => state.unreadCount > 0);
};

/** Select notification loading state. */
export const useNotificationLoading = (): boolean => {
  return useNotificationStore((state) => state.isLoading);
};

/** Select notification error. */
export const useNotificationError = (): string | null => {
  return useNotificationStore((state) => state.error);
};

/** Select notification actions. Stable reference. */
export const useNotificationActions = () => {
  return useNotificationStore((state) => ({
    setNotifications: state.setNotifications,
    addNotification: state.addNotification,
    removeNotification: state.removeNotification,
    markAsRead: state.markAsRead,
    markAllAsRead: state.markAllAsRead,
    clearAll: state.clearAll,
    setLoading: state.setLoading,
    setError: state.setError,
    recalculateUnreadCount: state.recalculateUnreadCount,
  }));
};